import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const apiUrl = process.env.REACT_APP_API_URL;

function AdminEditBlog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState(null);
  const [existingImage, setExistingImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchBlog();
  }, []);

  const fetchBlog = async () => {
    setLoading(true);
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${apiUrl}/api/admin/blogs/${id}`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        const blog = response.data.blog;
        setTitle(blog.title);
        setSummary(blog.summary);
        setContent(blog.content);
        setExistingImage(blog.featured_image);
      } else {
        setError(response.data.message || 'Failed to fetch blog.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching blog.');
    } finally {
      setLoading(false);
    }
  };

  const handleFeaturedImageChange = (e) => {
    setFeaturedImage(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const formData = new FormData();
    formData.append('title', title);
    formData.append('summary', summary);
    formData.append('content', content);
    if (featuredImage) formData.append('featured_image', featuredImage);

    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('Authentication required.');
        return;
      }

      const response = await axios.put(`${apiUrl}/api/admin/blogs/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${sessionData.token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.success) {
        setSuccess('Blog updated successfully.');
        setTimeout(() => navigate('/admin-blogs'), 2000);
      } else {
        setError(response.data.message || 'Failed to update blog.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating blog.');
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Edit Blog</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="card overflow-hidden">
            <div className="card-body p-24">
              <h4 className="mb-16">Edit Blog</h4>
              {error && <div className="alert alert-danger mb-16">{error}</div>}
              {success && <div className="alert alert-success mb-16">{success}</div>}
              {loading ? (
                <div className="alert alert-info">Loading blog...</div>
              ) : (
                <form onSubmit={handleSubmit} encType="multipart/form-data">
                  <div className="mb-3">
                    <label htmlFor="title" className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="summary" className="form-label">Summary</label>
                    <textarea
                      className="form-control"
                      id="summary"
                      value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                      required
                    ></textarea>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="content" className="form-label">Content</label>
                    <CKEditor
                      editor={ClassicEditor}
                      data={content}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        setContent(data);
                      }}
                      config={{
                        toolbar: [
                          'heading', '|',
                          'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                          'undo', 'redo'
                        ],
                        height: '300px' // Set desired height
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="featured_image" className="form-label">Featured Image</label>
                    {existingImage && (
                      <img
                        src={`${apiUrl}/${existingImage}`}
                        alt="Existing Featured Image"
                        style={{ width: '200px', height: 'auto', marginBottom: '10px' }}
                      />
                    )}
                    <input
                      type="file"
                      className="form-control"
                      id="featured_image"
                      accept="image/*"
                      onChange={handleFeaturedImageChange}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary">Update Blog</button>
                </form>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminEditBlog;
